// GameDirector — single owner of the game simulation. Instantiates every
// time-driven system and wires it to the EventBus. React components must
// never mutate game state from time events; they only read and render.

import { EconomySystem } from './systems/EconomySystem'
import { WeatherSystem } from './systems/WeatherSystem'
import { EventSystem } from './systems/EventSystem'
import { TournamentSystem } from './systems/TournamentSystem'
import { RosterSystem } from './systems/RosterSystem'
import { InjurySystem } from './systems/InjurySystem'
import { LeagueSystem } from './systems/LeagueSystem'
import { CircuitSystem } from './systems/CircuitSystem'
import { GameStateSystem } from './systems/GameStateSystem'
import { MaintenanceSystem } from './systems/MaintenanceSystem'
import { BoardSystem } from './systems/BoardSystem'
import { PrestigeSystem } from './systems/PrestigeSystem'
import { ConstructionSystem } from './systems/ConstructionSystem'
import { StorySystem } from './systems/StorySystem'
import { AutoSaveSystem } from './systems/AutoSaveSystem'

export class GameDirector {
  private static instance: GameDirector | null = null

  private economy: EconomySystem | null = null
  private weather: WeatherSystem | null = null
  private events: EventSystem | null = null
  private tournaments: TournamentSystem | null = null
  private roster: RosterSystem | null = null
  private injuries: InjurySystem | null = null
  private league: LeagueSystem | null = null
  private circuit: CircuitSystem | null = null
  private gameState: GameStateSystem | null = null
  private maintenance: MaintenanceSystem | null = null
  private board: BoardSystem | null = null
  private prestige: PrestigeSystem | null = null
  private construction: ConstructionSystem | null = null
  private story: StorySystem | null = null
  private autoSave: AutoSaveSystem | null = null

  private constructor() {}

  static getInstance(): GameDirector {
    if (!GameDirector.instance) {
      GameDirector.instance = new GameDirector()
    }
    return GameDirector.instance
  }

  get isRunning(): boolean {
    return this.economy !== null
  }

  init(): void {
    if (this.isRunning) return
    this.economy = new EconomySystem()
    this.weather = new WeatherSystem()
    this.events = new EventSystem()
    this.tournaments = new TournamentSystem()
    this.roster = new RosterSystem()
    this.injuries = new InjurySystem()
    this.league = new LeagueSystem()
    this.circuit = new CircuitSystem()
    this.gameState = new GameStateSystem()
    this.maintenance = new MaintenanceSystem()
    this.board = new BoardSystem()
    this.prestige = new PrestigeSystem()
    this.construction = new ConstructionSystem()
    this.story = new StorySystem()
    // Last, so its DAY_CHANGED snapshot reflects the fully-processed day.
    this.autoSave = new AutoSaveSystem()
  }

  destroy(): void {
    this.economy?.destroy()
    this.weather?.destroy()
    this.events?.destroy()
    this.tournaments?.destroy()
    this.roster?.destroy()
    this.injuries?.destroy()
    this.league?.destroy()
    this.circuit?.destroy()
    this.gameState?.destroy()
    this.maintenance?.destroy()
    this.board?.destroy()
    this.prestige?.destroy()
    this.construction?.destroy()
    this.story?.destroy()
    this.autoSave?.destroy()
    this.economy = null
    this.weather = null
    this.events = null
    this.tournaments = null
    this.roster = null
    this.injuries = null
    this.league = null
    this.circuit = null
    this.gameState = null
    this.maintenance = null
    this.board = null
    this.prestige = null
    this.construction = null
    this.story = null
    this.autoSave = null
  }
}
